import { useSubmitEnrollmentJointSelectAccountData } from '@egw/api'
import { ActionTile, Alert, AlertType, GenericError, ProgressBar } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { enrollmentCookieService } from '@egw/enrollment-cookie'
import { type FeatureFlags } from '@egw/constants'
import { useFlags } from '@egw/feature-flags'
import { useTranslation } from '@egw/i18n'
import { EnrollmentJointLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { riskService } from '@egw/risk-service'
import { UserIcon, UsersIcon } from '@heroicons/react/24/solid'
import { useEffect, type FC } from 'react'
import { useNavigate } from 'react-router'
import { PageHeader } from '../../../components'
import { ApexDisclosures } from '../../../components/get-started-enrollment-disclosures/apex-disclosures'
import { ROUTES as GENERIC_ROUTES } from '../../../constants/routes/routes-generic'
import { ROUTES as JOINT_ROUTES } from '../../../constants/routes/routes-joint'
import { ROUTES as MULTI_ROUTES } from '../../../constants/routes/routes-multi'

const log = new Logger('Enrollment - Joint - SelectAccount')

export const SelectAccountPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()
  const { flags } = useFlags<FeatureFlags>()
  const isV2Enabled = flags.rolloutJointEnrollmentV2
  const progress = 5

  useEffect(() => {
    // sendEvent(/* TODO: ADD EVENT FOR LANDING ON PAGE */)
  }, [])

  // const { data, isLoading, error } = useGetEnrollmentJointSelectAccountData() // Might not be needed
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentJointSelectAccountData()

  // Log form submission errors
  useEffect(() => {
    if (submitError) {
      log.error('Error submitting enrollment joint select account form', submitError)
    }
  }, [submitError])

  // Protect route
  // useValidateRoute(data?.path) // No route protection here

  const onSubmit = async (): Promise<void> => {
    try {
      if (isV2Enabled) {
        // Reset enrollment session before initiating application
        await enrollmentCookieService.resetSession()
        await riskService.updateFlow('enrollment')
        const response = await mutate({
          initiateApplication: true,
        })
        if (response?.activePerson?.path) {
          void navigate(response.activePerson.path)
        } else {
          void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${GENERIC_ROUTES.ERROR.path}`)
        }
      } else {
        void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${JOINT_ROUTES.ACCOUNT_TYPE.path}`)
      }
    } catch (e) {
      log.error('Error submitting select account form', e)
    }
  }
  // AUTO GENERATED FORM HANDLERS END

  return (
    <EnrollmentJointLayout title={t('joint.select_account.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {/* TODO: Keeping loading until BE is defined */}
      {/* {isLoading ? <EnrollmentPageLoading testId="select_account-loading" /> : null} */}

      <div className="w-full flex-1 flex flex-col justify-start">
        {/* AUTO GENERATED COMPONENTS START*/}
        <PageHeader title={t('joint.select_account.title')} />
        <ProgressBar className="mb-md" progressPercentage={(1 / 13) * 100} showAnimation testId="progress-bar" />
        <p className="typo-primary-base text-text-meta mb-md" data-test-id="description_1">
          {t('joint.select_account.description_1')}
        </p>
        <div className="w-full flex-1 flex flex-col justify-start">
          <span className="typo-primary-base-semibold text-text-header mb-md">
            {t('joint.select_account.selection_title')}
          </span>
          <div className="flex flex-col">
            <ActionTile
              className="mb-md"
              title={t('joint.select_account.individual_title')}
              description=""
              icon={<UserIcon className="w-6 h-6 mr-sm" />}
              onClick={() => {
                navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${MULTI_ROUTES.MULTI_HOME.path}`)
              }}
              testId="individual-account-tile"
            />
            <ActionTile
              title={t('joint.select_account.joint_title')}
              description=""
              icon={<UsersIcon className="w-6 h-6 mr-sm mb-0" />}
              onClick={onSubmit}
              testId="joint-account-tile"
            />

            <div className="mt-5xl">
              <ApexDisclosures />
            </div>
          </div>

          <div className="flex-1 md:hidden" />

          {/* AUTO GENERATED COMPONENTS */}

          {submitError ? (
            <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" />
          ) : null}
        </div>
        {/* AUTO GENERATED COMPONENTS END*/}
      </div>

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {submitError ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentJointLayout>
  )
}
